import { Stethoscope } from 'lucide-react';
import { lazyComponent } from '@/platform/lazyComponent';
import type { PlatformModule } from '@/platform/modules';
import { medicalAthleteRoutes, medicalRoutes } from './routes';

const MedicalDashboard = lazyComponent(() => import('./MedicalDashboard').then((module) => module.MedicalDashboard));
const MedicalAlertCard = lazyComponent(() => import('./athlete/MedicalAlertCard').then((module) => module.MedicalAlertCard));

/**
 * Medical: the doctor's workspace. It stands on Athletes and Base and never on Physiotherapy: what a doctor sees of Physio's records comes through the
 * permissions the doctor role holds, and Physio's own screens. It adds its tab, an alerts card and a dashboard through the hooks the athlete's page offers,
 * so with this module uninstalled nothing of it remains anywhere. Every route and tab requires an explicit Medical permission (deny by default); an
 * administrator holds none of them, and a coach never does.
 */
export const medicalModule: PlatformModule = {
  id: 'medical',
  nav: [{ id: 'medical', label: 'Medical', to: '/medical', icon: Stethoscope, permission: 'medical.view', section: 'module', order: 45 }],
  routes: medicalRoutes,
  // The Medical tab of the athlete's page, which Athletes owns (its route has the id `athlete`).
  routeExtensions: { athlete: medicalAthleteRoutes },
  // Home for whoever may change medical records (the doctor). Read-only holders (a physio) keep the home they had, and every holder reaches the dashboard from the menu.
  home: { order: 5, permission: 'medical.manage', component: MedicalDashboard },
  athleteTabs: [{ id: 'medical', label: 'Medical', path: 'medical', permission: 'medical.view', order: 25, icon: Stethoscope }],
  slots: {
    'athlete.overview.side': [{ id: 'medical-alerts', order: 5, permission: 'medical.view', component: MedicalAlertCard }],
  },
  access: {
    title: 'Medical',
    order: 30,
    levels: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: "Read an athlete's medical profile, allergies and conditions.",
        perms: ['medical.view', 'medical_allergy.view', 'medical_condition.view'],
      },
      {
        id: 'doctor',
        label: 'Doctor',
        description: 'Also change the medical profile and record and correct allergies and conditions.',
        perms: ['medical.manage', 'medical_allergy.manage', 'medical_condition.manage'],
      },
    ],
    extras: [
      {
        id: 'all-athletes',
        label: 'All athletes',
        description: 'Reach the medical records of every athlete, not only those on the teams they are assigned to.',
        perms: ['medical.scope_all'],
      },
    ],
    permissions: {
      'medical.view': "See the Medical dashboard and an athlete's medical profile",
      'medical.manage': "Change an athlete's medical profile",
      'medical_allergy.view': 'See allergies',
      'medical_allergy.manage': 'Record and correct allergies',
      'medical_condition.view': 'See medical conditions',
      'medical_condition.manage': 'Record and correct medical conditions',
      'medical.scope_all': 'See the medical records of every athlete, not only those of the teams assigned to them',
    },
  },
};
